import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';

import CollapsableTagContainer from '@polyai/ui/components/atoms/CollapsableTagContainer';

import type { BaseColumnProps } from './BaseColumn';
import BaseColumn from './BaseColumn';
import * as Styled from './JupiterCard.styled';

interface Props {
  icon?: ReactNode;
  iconWrapperClassName?: string;
  customIcon?: ReactNode;
  title: string;
  titleTooltip?: ReactNode;
  titleTooltipProps?: BaseColumnProps['titleTooltipProps'];
  value?: string | number | string[] | null;
  valueTooltip?: ReactNode;
  valueTooltipProps?: BaseColumnProps['valueTooltipProps'];
  valueTooltipDisableUnderline?: boolean;
  customValue?: ReactNode;
  isLoading?: boolean;
  className?: string;
  missingValueText?: string;
}

const ValueColumn: FC<Props> = ({
  icon,
  iconWrapperClassName,
  customIcon,
  title,
  titleTooltip,
  value,
  valueTooltip,
  valueTooltipProps,
  valueTooltipDisableUnderline,
  customValue,
  isLoading = false,
  className,
  missingValueText = '-',
  ...props
}) => {
  const isValueArray = Array.isArray(value);
  const isMissing = isValueArray
    ? !value.length ||
      value.every(item => ([null, undefined] as any[]).includes(item))
    : ([null, undefined] as any[]).includes(value);

  const content = useMemo(() => {
    if (isMissing) {
      return (
        <Styled.CardValueMissing>{missingValueText}</Styled.CardValueMissing>
      );
    }
    if (isValueArray) {
      return <CollapsableTagContainer tags={value as string[]} />;
    }
    return <Styled.CardValue>{value}</Styled.CardValue>;
  }, [isMissing, missingValueText, isValueArray, value]);

  return (
    <BaseColumn
      className={className}
      customIcon={customIcon}
      icon={icon}
      iconWrapperClassName={iconWrapperClassName}
      isLoading={isLoading}
      title={<Styled.ValueCaption>{title}</Styled.ValueCaption>}
      titleTooltip={titleTooltip}
      value={customValue || content}
      valueTooltip={customValue ? undefined : valueTooltip}
      valueTooltipDisableUnderline={valueTooltipDisableUnderline}
      valueTooltipProps={valueTooltipProps}
      {...props}
    />
  );
};

export default ValueColumn;
